<template>
    <BaseModal
        center
        :is-open="modalStore.modals.feedbackModal"
        @close="closeModal">
        <button
            style="
                cursor:
                    url(/assets/images/ui/icons/jpg-png/close-cursor.png),
                    pointer;
            "
            @click="closeModal"
            class="absolute inset-0 size-full outline-none" />
        <div
            class="relative rounded-20 bg-gradient-to-tr from-accent to-white/20 px-3 py-10 max-md:h-full md:p-20">
            <AtomTitle class="max-md:text-20/120">
                Наш специалист свяжется с&nbsp;Вами и&nbsp;проконсультирует
                по&nbsp;всем вопросам
            </AtomTitle>
            <form class="flex flex-col pt-5 md:pt-10">
                <div class="flex max-lg:flex-col max-lg:space-y-5 lg:space-x-5">
                    <div class="flex w-full flex-col space-y-5">
                        <AtomField
                            v-model="userData.firstName"
                            placeholder="Ваше имя"
                            :error="errors.firstName">
                        </AtomField>
                        <AtomField
                            v-model="userData.phone"
                            placeholder="Телефон"
                            :error="errors.phone"
                            @input="validatePhone(userData, errors, $event)">
                        </AtomField>
                        <AtomField
                            v-model="userData.email"
                            placeholder="Электронная почта"
                            :error="errors.email"
                            @input="validateEmail(userData, errors)">
                        </AtomField>
                    </div>
                    <AtomTextarea
                        v-model="userData.message"
                        placeholder="Комментарий"
                        :error="errors.message"
                        class="min-h-32 w-full max-sm:min-h-40"></AtomTextarea>
                </div>
                <div
                    class="flex items-center pt-5 max-md:flex-col max-md:space-y-5 md:space-x-5 lg:ml-auto">
                    <AtomButton
                        class="w-full"
                        @click="handleForm">
                        Отправить
                    </AtomButton>

                    <BaseCheckbox
                        required
                        :error="errors.termsAccepted"
                        v-model="userData.termsAccepted"
                        class="!w-full shrink-0 md:max-w-[220px]"
                        @click="errors.termsAccepted = false">
                        <span
                            class="inline-block text-12/140 text-ash-200 max-md:text-left">
                            Я даю&nbsp;согласие на
                            <NuxtLink
                                target="_blank"
                                href="/pdf/soglasie.pdf"
                                class="underline underline-offset-4">
                                обработку персональных данных
                            </NuxtLink>
                        </span>
                    </BaseCheckbox>
                </div>
            </form>
            <div
                :style="`height: ${heightErrorBlock}px`"
                class="overflow-hidden pt-2 transition-all duration-200 md:pt-5">
                <div
                    ref="blockMessageError"
                    class="rounded-8 border border-red-600 bg-red-600/20 px-4 py-3 backdrop-blur-sm">
                    <p class="text-center text-12/140">
                        Чтобы отправить форму, нужно заполнить все поля
                    </p>
                </div>
            </div>
            <button
                class="absolute right-1 top-1 size-14 max-md:size-10 md:right-5 md:top-5"
                @click="closeModal">
                <svg
                    viewBox="0 0 70 70"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    width="70"
                    height="70"
                    class="size-14 max-md:size-10">
                    <path
                        d="M48.5 48.5L21 21"
                        stroke="white"></path>
                    <path
                        d="M48.5 21L21 48.5"
                        stroke="white"></path>
                </svg>
            </button>
        </div>
    </BaseModal>
</template>

<script setup lang="ts">
import { YandexKey } from '~/const/yandex-key'
import type { Category } from '~/types/global.types'

import AtomButton from '~/components/atom/atom-button/AtomButton.vue'
import AtomField from '~/components/atom/atom-field/AtomField.vue'
import AtomTextarea from '~/components/atom/atom-textarea/AtomTextarea.vue'
import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import BaseCheckbox from '~/components/base/base-checkbox/BaseCheckbox.vue'
import BaseModal from '~/components/base/base-modal/BaseModal.vue'
import type { SettingCallback } from '~/components/modals/feedback-modal/feedback.types'

import type { Page } from '~/composables/useApiFetch.composables'

import { errorHandling } from '~/utils/error-handling'
import { validateEmail, validatePhone } from '~/utils/validations'

const route = useRoute()
const nuxtApp = useNuxtApp()

const { data: forms } = await useApiFetch<Page<SettingCallback[]>>(
    '/v1/setting-callbacks',
    {
        lazy: true,
        getCachedData(key) {
            return nuxtApp.payload?.data?.[key]
        },
    }
)

const modalStore = useModalStore()
const blockMessageError = ref<HTMLDivElement | null>(null)

const CATEGORIES = [
    { slug: 'radioguides', title: 'радиогидов' },
    { slug: 'audioguides', title: 'аудиогидов' },
    { slug: 'interpretation', title: 'оборудования синхронного перевода' },
    { slug: 'cabins', title: 'кабин для синхронных переводчиков' },
    { slug: 'conference-systems', title: 'конференц-систем' },
    { slug: 'dostupnaia-sreda', title: 'радиооборудования Доступная среда' },
    { slug: 'arenda-zvuka', title: 'звукового оборудования' },
    {
        slug: 'arenda-oborudovanie-dlya-sinhronnogo-perevoda',
        title: 'оборудования синхронного перевода',
    },
    {
        slug: 'arenda-konferents-system',
        title: 'конференц-систем',
    },
    { slug: 'arenda-video', title: 'видеооборудования' },
    { slug: 'arenda-radioguides', title: 'радиогидов' },
    { slug: 'arenda-audioguides', title: 'аудиогидов' },
]

const DIRECTIONS = [
    {
        slug: 'radioguide',
        title: 'радиогидам Reinvox',
        second_title: 'радиогидов Reinvox',
    },
    {
        slug: 'audioguide',
        title: 'аудиогидам SPBAUDIO',
        second_title: 'аудиогидов SPBAUDIO',
    },
    {
        slug: 'sinhroperevod',
        title: 'оборудованию синхронного перевода SPBAUDIO',
        second_title: 'оборудования синхронного перевода SPBAUDIO',
    },
    {
        slug: 'arenda-oborudovanie-dlya-sinhronnogo-perevoda',
        title: 'оборудованию синхронного перевода',
        second_title: 'оборудования синхронного перевода',
    },
    {
        slug: 'arenda-zvuka',
        title: 'звуковому оборудованию',
        second_title: 'звукового оборудования',
    },
    {
        slug: 'arenda-video',
        title: 'видеооборудованию и организация онлайн-мероприятий',
        second_title: 'видеооборудования и организация онлайн-мероприятий',
    },
    {
        slug: 'arenda-radioguides',
        title: 'радиогидам',
        second_title: 'радиогидов',
    },
    {
        slug: 'arenda-audioguides',
        title: 'аудиогидам',
        second_title: 'аудиогидов',
    },
    {
        slug: 'cabin',
        title: 'кабинам для синхронных переводчиков SPBAUDIO',
        second_title: 'кабин для синхронных переводчиков SPBAUDIO',
    },
    {
        slug: 'conference-system',
        title: 'конференц-системам Reinvox',
        second_title: 'конференц-систем Reinvox',
    },
    {
        slug: 'arenda-konferents-system',
        title: 'конференц-системам Bosch',
        second_title: 'конференц-систем Bosch',
    },
    {
        slug: 'dostupsreda',
        title: 'радиооборудованию Доступная среда',
        second_title: 'радиооборудования Reinvox',
    },
]

const newMessageForm = computed<string>(() => {
    if (modalStore.feedbackMessage) {
        return modalStore.feedbackMessage
    }

    if (!forms.value) return ''

    const dataByFormId = forms.value.data.filter(
        (form) => form.id == modalStore.formID
    )

    if (modalStore.formID === 8) {
        const slugsCategories = [...modalStore.categories]
            .sort(
                (a: Category, b: Category) =>
                    (a.ordering || 0) - (b.ordering || 0)
            )
            .map((category: Category) => category.slug)

        const categories = CATEGORIES.filter((item) =>
            slugsCategories.includes(item.slug)
        )
            .map((category) => category.title)
            .join(', ')

        modalStore.setNameProduct(categories)
    } else if ([1, 3, 14, 15].includes(modalStore.formID)) {
        const slugDirection = route.fullPath.split('/')[1] || ''

        const directionData = DIRECTIONS.filter((direction) =>
            direction.slug.includes(slugDirection)
        )

        modalStore.setNameProduct(
            modalStore.formID === 14
                ? directionData[0].title
                : directionData[0].second_title
        )
    }

    const changeName =
        ![5, 6].includes(modalStore.formID) && modalStore.feedbackLabel
            ? modalStore.feedbackLabel
            : modalStore.nameProduct

    let message = ''
    message = dataByFormId[0]?.message
        ? dataByFormId[0]?.message.replace('<NAME>', changeName)
        : ''

    return message
})

const userData = reactive({
    firstName: '',
    phone: '',
    email: '',
    message: newMessageForm.value,
    termsAccepted: false,
})

const errors = reactive({
    firstName: '',
    phone: '',
    email: '',
    message: '',
    termsAccepted: false,
})

watch(
    () => modalStore.modals.feedbackModal,
    (newVal) => {
        if (!newVal) return

        if (isCromi()) {
            //  если айди формы не установлено, то ставим "дефолтные"
            if (modalStore.formID === 0) {
                switch (route.name) {
                    case 'rent':
                        modalStore.setFormID(11)
                        break
                    case 'direction':
                        modalStore.setFormID(14)
                        break
                    case 'catalog-product':
                        modalStore.setFormID(6)
                        break
                    case 'portfolio-slug':
                        modalStore.setFormID(8)
                        break
                    default:
                        modalStore.setFormID(7)
                }
            }
        } else if (isRent()) {
            //  если айди формы не установлено, то ставим "дефолтные"
            if (modalStore.formID === 0) {
                switch (route.name) {
                    case 'sale':
                        modalStore.setFormID(7)
                        break
                    case 'direction':
                        modalStore.setFormID(15)
                        break
                    case 'portfolio-slug':
                        modalStore.setFormID(8)
                        break
                    default:
                        modalStore.setFormID(11)
                }
            }
        } else if (isRadioguide()) {
            //  если айди формы не установлено, то ставим "дефолтные"
            if (modalStore.formID === 0) {
                switch (route.name) {
                    case 'sale':
                        modalStore.setFormID(7)
                        break
                    case 'product':
                        modalStore.setFormID(13)
                        break
                    default:
                        modalStore.setFormID(12)
                }
            }
        }
    }
)

watch(
    () => newMessageForm.value,
    () => {
        userData.message = newMessageForm.value
    }
)

const heightErrorBlock = computed(() => {
    if (!blockMessageError.value) return 0

    return errors.firstName || errors.phone || errors.email || errors.message
        ? blockMessageError.value?.getBoundingClientRect().height + 20
        : 0
})
async function handleForm(e: FormDataEvent) {
    e.preventDefault()
    if (!forms.value) return

    validateData()

    if (
        !errors.firstName.length &&
        !errors.phone.length &&
        !errors.email.length &&
        !errors.message.length &&
        !errors.termsAccepted
    ) {
        let message = ''
        for (const callback of forms.value.data) {
            if (
                modalStore.typeForm &&
                callback.name.includes(modalStore.typeForm)
            ) {
                if (!modalStore.nameProduct) {
                    message = `${callback.message} ${userData.message}`
                    return
                }

                message =
                    callback.message.replace(
                        '&lt;NAME&gt;',
                        modalStore.nameProduct
                    ) +
                    ' ' +
                    userData.message
            }
        }

        message = message.length
            ? message.replace('<p>', '').replace('</p>', '')
            : userData.message

        const utmParams = useCookie<Record<string, any> | null>('utm')

        const { data, error } = await useApiFetch('/v1/callbacks', {
            method: 'POST',
            key: `callback-${Date.now()}`,
            body: JSON.stringify({
                name: userData.firstName,
                phone: userData.phone,
                href: location.href,
                form: modalStore.nameForm,
                email: userData.email,
                message,
            }),
            query: utmParams.value || undefined,
        })

        if (error.value && error.value?.data) {
            errorHandling(error.value, errors)
            return
        }

        if (data.value) {
            try {
                ym(YandexKey(), 'reachGoal', 'vseformi')
            } catch (error) {
                console.error(error)
            }
        }
        userData.firstName = ''
        userData.phone = ''
        userData.email = ''
        userData.message = ''
        closeModal()
    }
}

function validateData() {
    errors.firstName = !userData.firstName.length ? 'Заполните поле' : ''

    if (!userData.phone.length) {
        errors.phone = 'Заполните поле'
    }

    errors.message = !userData.message.length ? 'Заполните поле' : ''

    if (!userData.termsAccepted) {
        errors.termsAccepted = true
    }

    if (!userData.email.length) {
        errors.email = 'Заполните поле'
    } else {
        const isEmail = new RegExp(/^\S+@\S+\.\S+$/, 'i')
        errors.email = isEmail.test(userData.email)
            ? ''
            : 'Неверный формат почты'
    }
}

function closeModal() {
    modalStore.close('feedbackModal')
    clearErrors()
}

function clearErrors() {
    errors.firstName = ''
    errors.phone = ''
    errors.email = ''
    errors.message = ''
    errors.termsAccepted = false
}
</script>
